import { Box, Stack, SxProps, TextField, Theme } from '@mui/material';
import {
  SearchFieldClearIconButton,
  SearchFieldSearchIconButton,
  SearchFieldSearchIconGraphic,
  searchFieldTriggerRowSx,
  SearchFieldIconMode,
} from 'Components/SearchFieldIcon';
import { KeyboardEvent, RefObject } from 'react';

interface IOutlinedSearchFieldProps {
  value: string;
  onChange: (value: string) => void;
  onClear: () => void;
  clearAriaLabel: string;
  ariaLabel: string;
  placeholder?: string;
  height?: string | number;
  borderRadius?: string | number;
  searchIconMode?: SearchFieldIconMode;
  searchAriaLabel?: string;
  onSearchActivate?: () => void;
  inputRef?: RefObject<HTMLInputElement | null>;
  onEnter?: () => void;
  onKeyDown?: (event: KeyboardEvent) => void;
  preventEnterDefault?: boolean;
  wrapperSx?: SxProps<Theme>;
  textFieldSx?: SxProps<Theme>;
  clearSize?: number | string;
  clearIconSrc?: string;
  onClearMouseDown?: (event: React.MouseEvent) => void;
  onClearClick?: (event: React.MouseEvent) => void;
  autoFocus?: boolean;
}

export const OutlinedSearchField = ({
  value,
  onChange,
  onClear,
  clearAriaLabel,
  ariaLabel,
  placeholder,
  height = '44px',
  borderRadius = '22px',
  searchIconMode = 'decorative',
  searchAriaLabel = ariaLabel,
  onSearchActivate,
  inputRef,
  onEnter,
  onKeyDown,
  preventEnterDefault = true,
  wrapperSx,
  textFieldSx,
  clearSize,
  clearIconSrc,
  onClearMouseDown,
  onClearClick,
  autoFocus = false,
}: IOutlinedSearchFieldProps) => {
  const hasValue = Boolean(value);

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Enter') {
      if (preventEnterDefault) {
        e.preventDefault();
      }
      onEnter?.();
    }
    onKeyDown?.(e);
  };

  const searchIcon =
    searchIconMode === 'button' && onSearchActivate ? (
      <SearchFieldSearchIconButton
        ariaLabel={searchAriaLabel}
        onActivate={onSearchActivate}
      />
    ) : (
      <SearchFieldSearchIconGraphic />
    );

  return (
    <Stack
      direction='row'
      alignItems='center'
      role='search'
      sx={{
        width: '100%',
        maxWidth: '100%',
        height,
        bgcolor: 'white',
        border: '1px solid',
        borderColor: 'primary.shade_200',
        borderRadius,
        cursor: 'text',
        ...searchFieldTriggerRowSx,
        ...wrapperSx,
      }}
    >
      {searchIcon}
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          height: '100%',
          display: 'flex',
          alignItems: 'center',
        }}
      >
        <TextField
          inputRef={inputRef}
          fullWidth
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          variant='outlined'
          onKeyDown={handleKeyDown}
          autoFocus={autoFocus}
          slotProps={{
            htmlInput: {
              'aria-label': ariaLabel,
              ...(autoFocus ? { 'data-autofocus': true } : {}),
            },
          }}
          sx={{
            '& .MuiOutlinedInput-root': { px: 0, height: '100%' },
            '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
            '& .MuiOutlinedInput-input:focus': { outline: 'none' },
            '& .MuiOutlinedInput-input': {
              textAlign: 'right',
              py: 0,
              px: 0,
              height: '100%',
              fontWeight: 400,
              color: 'text.primary',
              '&::placeholder': { opacity: 1, color: 'text.disabled' },
            },
            ...textFieldSx,
          }}
        />
      </Box>
      {hasValue && (
        <SearchFieldClearIconButton
          ariaLabel={clearAriaLabel}
          onClear={onClear}
          size={clearSize}
          iconSrc={clearIconSrc}
          onMouseDown={onClearMouseDown}
          onClick={onClearClick}
        />
      )}
    </Stack>
  );
};
